import * as React from "react";
import {Button, Col, ControlLabel, FormControl, Modal, Row} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Spinner} from "../common/ui/spinner/Spinner";
import {setStateAsync} from "../common/util/ReactUtils";
import {generateUrl} from "../common/util/Util";
import {Service, ServiceProviderProperties} from "../services/model";
import {StyleEditorCommand, StyleEditorMessage} from "./model";
import axios from "axios";
import {Container} from "@material-ui/core";
import {WithApi, WithApiProperties} from "../common/util/WithApi";

const LABEL_COL = 3;
const CONTROL_COL = 9;
const MESSAGES = defineMessages({
    ERROR_NO_WFS: {
        id: "studio.style-editor.error-no-wfs",
        defaultMessage: "No available WFS Services",
    }
});

export interface StyleEditorPopupOwnProps {
    onClose: () => void;
}

type StyleEditorPopupProps = StyleEditorPopupOwnProps & InjectedIntlProps;

interface StyleEditorPopupState {
    editorReady: boolean;
    service: Service;
    updateService: boolean;
    services: Service[];
    loading: boolean;
    error?: string;
    styleName: string;
    maxFeatures: number;
}

class StyleEditorPopupComponent extends React.Component<StyleEditorPopupProps & WithApiProperties, StyleEditorPopupState> {

    private iframeRef = React.createRef<HTMLIFrameElement>();
    private serviceDetailsPromise: Promise<ServiceProviderProperties>;
    private styleEditorMountPromise: Promise<void>;

  constructor(props) {
        super(props);

        this.state = {
            editorReady: false,
            loading: true,
            service: null,
            updateService: true,
            services: [],
            styleName: "",
            maxFeatures: null,
        };
    }

    componentDidMount() {
        const {intl} = this.props;

        this.updateState({loading: true});
        Promise.allSettled([
          axios.get(`${this.props.api.getStudioApiUrl()}/configuration`),
          axios.get(
              `${this.props.api.getStudioApiUrl()}/services?page=1&pageSize=65535&sortBy=creationTime&sortOrder=ASC&type=wfs`)
        ]).then((response) => {
            let maxFeatures = null;
            let services = null;
            if (response[0].status === 'fulfilled') {
                const maxFeaturesResponse = response[0] as PromiseFulfilledResult<any>;
                const parsed = parseInt(maxFeaturesResponse.value.data.values["vectorEditorMaxFeatures"], 10);
                maxFeatures = isNaN(parsed) ? null : parsed;
            }
            if (response[1].status === 'fulfilled') {
                const servicesResponse = response[1] as PromiseFulfilledResult<any>;
                services = servicesResponse.value.data.services;
            }
            if (services && services.length > 0) {
                this.updateState({loading: false, maxFeatures, services, service: services[0]});
            } else {
                this.updateState({loading: false, error: intl.formatMessage(MESSAGES.ERROR_NO_WFS)});
            }
        });
    }

    componentDidUpdate() {
        const {service, updateService} = this.state;
        if (service && updateService) {
          this.serviceDetailsPromise = axios.get(
              `${this.props.api.getStudioApiUrl()}/services/${service.id}`).then(r => r.data.service);
            this.styleEditorMountPromise = new Promise((resolve) => {
                const listener = (e) => {
                    if (e.data.command === StyleEditorCommand.MOUNTED) {
                        window.removeEventListener("message", listener);
                        resolve();
                    }
                };
                window.addEventListener("message", listener);
            });
            this.updateState({updateService: false})
        }
    }

    updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

    render() {
        const {onClose} = this.props;
        const {loading, error, styleName, editorReady, maxFeatures} = this.state;


        return (
            <Modal className="styleEditorPopup" show={true} onHide={onClose} backdrop={"static"} keyboard={false}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>
                        <FormattedMessage id="studio.style.vector-editor.title"
                                          defaultMessage="Vector Style Editor"/>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="styleEditorBodyContent">
                        {error && this.renderError()}
                        {!error && !editorReady && this.renderSelectService()}
                        {loading && this.renderLoading()}
                        {editorReady &&
                            <iframe ref={this.iframeRef}
                                    src={generateUrl(`/style-editor?newStyle=${styleName}&maxFeatures=${maxFeatures}`)}
                                    onLoad={this.onLoad}/>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {this.renderButtons()}
                </Modal.Footer>
            </Modal>
        );

    }

    private renderSelectService() {
        const {services} = this.state;
        const serviceOptions: JSX.Element[] = [];
        services.forEach((svc) => {
            const option = <option key={`key_${svc.name}`} value={svc.id}>{svc.title}</option>;
            serviceOptions.push(option);
        });
        return (
            <Container>
                <Row>
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.style.vector-editor.services"
                                          defaultMessage="Select Sample Service"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="sampleWFS" componentClass="select" onChange={this.onChangeService}>
                            {serviceOptions}
                        </FormControl>
                    </Col>
                </Row>
                <Row>
                    <Col componentClass={ControlLabel} sm={LABEL_COL}>
                        <FormattedMessage id="studio.style.vector-editor.new-name"
                                          defaultMessage="Enter name for new Style"/>
                    </Col>
                    <Col sm={CONTROL_COL}>
                        <FormControl name="styleName" onChange={this.onChangeStyleName}/>
                    </Col>
                </Row>
            </Container>);
    }

    private onChangeService = (event: any) => {
        const {services} = this.state;
        const service = services.find(svc => {
            return svc.id === event.target.value
        });
        if (service) {
            this.updateState({service, updateService: true});
        }
    }

    private onChangeStyleName = (event: any) => {
        this.updateState({styleName: event.target.value});
    }

    private renderError = () => {
        const {error} = this.state;
        return (
            <Container>
                <Row>
                    {error}
                </Row>
            </Container>
        );
    }

    private renderButtons() {
        const {onClose} = this.props;
        const {editorReady, loading, styleName, service} = this.state;
        if (!editorReady) {
            return (
                <div>
                    {!loading && styleName !== "" && service !== null &&
                        <Button onClick={this.openVectorEditor}>
                            <FormattedMessage id="studio.style.vector-editor.create" defaultMessage="Create New Style"/>
                        </Button>
                    }
                    <Button onClick={onClose}>
                        <FormattedMessage id="studio.form.close" defaultMessage="Close"/>
                    </Button>
                </div>
            );
        }

        return (
            <div>
                <Button onClick={this.save}>
                    <FormattedMessage id="studio.form.save" defaultMessage="Save"/>
                </Button>

                <Button onClick={onClose}><FormattedMessage id="studio.form.close"
                                                            defaultMessage="Close"/></Button>
            </div>
        );
    }

    private openVectorEditor = () => {
        const {styleName, service} = this.state;
        this.updateState({editorReady: styleName !== "" && service !== null});
    }

    private postMessage(message: StyleEditorMessage) {
        this.iframeRef.current.contentWindow.postMessage(message, window.location.origin);
    }

    private save = () => {
        this.postMessage({
            command: StyleEditorCommand.SAVE,
        });
    };

    private renderLoading() {
        return (
            <Row style={{textAlign: "center"}}>
                <h2>
                    <FormattedMessage id="studio.form.loading"
                                      defaultMessage="Loading..."/>
                </h2>
                <Spinner bigStyle/>
            </Row>
        );
    }

    private onLoad = () => {
        this.serviceDetailsPromise
            .then(() => {
                return setStateAsync(this, {
                    loading: false,
                });
            })
            .then(() => this.styleEditorMountPromise)
            .then(() => this.serviceDetailsPromise)
            .then((serviceDetails) => {
                this.postMessage({
                    command: StyleEditorCommand.INIT,
                    url: serviceDetails.endpointUrl,
                } as StyleEditorMessage);
            });
    };
}

export const StyleEditorPopup = injectIntl(WithApi(StyleEditorPopupComponent));